import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    contentChild,
    EventEmitter,
    inject,
    input,
    Input,
    InputSignal,
    OnDestroy,
    OnInit,
    Output,
    Signal,
    TemplateRef,
    Type
} from '@angular/core';

import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import {
    catchError,
    debounceTime,
    filter,
    map,
    shareReplay,
    startWith,
    switchMap,
    take,
    takeUntil,
    tap,
    withLatestFrom
} from 'rxjs/operators';

import { animate, style, transition, trigger } from '@angular/animations';
import { LanguageModule } from '@language';
import { ApiQuery, Restriction } from '../../../core/api/entities/api-query.entity';
import { BaseContainer } from '../../../core/common/containers/base.abstract.container';
import { BaseNestedFieldDefinition, DottedKey } from '../../../core/field-definitions/entities/field-definition.entity';
import { FieldDefinitionHelper } from '../../../core/field-definitions/helpers/field-definition.helper';
import { ShFieldDefinitionService } from '../../../core/field-definitions/services/field-definition.service';
import { ExtendedFacetProfileField } from '../../../core/profiles/entities/extended-profile-field.entity';
import { ExtendedFacetProfile } from '../../../core/profiles/entities/extended-profile.entity';
import { FacetProfileField } from '../../../core/profiles/entities/profile-field.entity';
import { FacetProfile } from '../../../core/profiles/entities/profile.entity';
import { ShUrlQueryService } from '../../../core/query/services/url-query.service';
import { DialogConfig } from '../../../dynamic-dialog/entities/dialog-config.entity';
import { DialogRef } from '../../../dynamic-dialog/entities/dialog-ref.class';
import { ShDynamicDialogService } from '../../../dynamic-dialog/services/dynamic-dialog.service';
import { DialogStyle } from '../../../elements/components/dialog/entities/dialog.entity';
import { ShFacetListComponent } from '../../../elements/components/facet-list/facet-list.component';
import { ShFacetBodyContext } from '../../../elements/components/facet/facet.component';
import { ShFacetPlaceHolderComponent } from '../../../elements/components/facet-placeholder/facet-placeholder.component';
import { ShFacetSummaryComponent } from '../../../elements/components/facet-summary/facet-summary.component';
import {
    ShFacet,
    ShSelectableFacetValue,
    ShSelectedFacetValue
} from '../../../elements/components/facet/entities/facet.entity';
import { LoadingBehaviorSubject } from '../../../helpers/loading-behavior-subject.helper';
import { ShFacetDataService } from '../../data-services/facet.data-service';
import { FacetRequest } from '../../entities/facet-request.entity';
import {
    ProfileFacetSearchDialogConfig,
    ProfileFacetSearchDialogOutput,
    ProfileFacetSearchDialogOutputSuccess
} from '../../entities/facet-search-dialog.entity';
import { createFacetRequest } from '../../helpers/facet-request.helper';
import { mergeFacetRanking, sortFacetsByOrder } from '../../helpers/facet-order.helper';
import { filterDiscriminating } from '../../helpers/hide-facets.helper';
import { ShFacetValueTranslationService } from '../../services/facet-value-translation.service';
import { ShSelectedFacetLabelService } from '../../services/selected-facet-label.service';
import { ShFacetPanelHeadDirective } from '../../directives/facet-panel-head.directive';
import { ShProfileFacetSearchDialogComponent } from '../profile-facet-search-dialog/profile-facet-search-dialog.component';

@Component({
    standalone: true,
    selector: 'sh-profile-facet-list',
    templateUrl: './profile-facet-list-container.component.html',
    styleUrls: ['./profile-facet-list-container.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, ShFacetSummaryComponent, ShFacetListComponent, ShFacetPlaceHolderComponent, LanguageModule],
    providers: [],
    animations: [
        // Prevent from appearing for 10ms
        trigger('fadeIn', [transition(':enter', [style({ display: 'none' }), animate('0ms 10ms', style({}))])]),
        // Prevent from disappearing for 10ms
        trigger('fadeOut', [transition(':leave', [style({}), animate('0ms 10ms', style({}))])])
    ]
})
export class ShProfileFacetListContainerComponent extends BaseContainer implements OnInit, OnDestroy {
    private query$!: Observable<ApiQuery>;

    // Current facet profile
    private readonly facetProfile$: ReplaySubject<FacetProfile> = new ReplaySubject<FacetProfile>(1);
    public extendedFacetProfile$: Observable<ExtendedFacetProfile>;
    public missingFacetProfile: boolean;

    // Currently selected facets
    public selectedFacets$!: Observable<ShSelectedFacetValue[]>;

    // Facet results with selected filtered out
    public selectableFacets$!: Observable<ShFacet[]>;

    // MED-3943 restrictions change when toggling to map view
    public restrictions$: BehaviorSubject<Restriction | undefined> = new BehaviorSubject<Restriction | undefined>(
        undefined
    );

    public readonly facetsLoading$: LoadingBehaviorSubject = new LoadingBehaviorSubject();

    protected readonly facetsService: ShFacetDataService = inject(ShFacetDataService);

    @Input() set facetProfile(facetProfile: FacetProfile) {
        if (facetProfile) {
            this.facetsLoading$.startLoading();
            this.missingFacetProfile = false;
            this.facetProfile$.next(facetProfile);
        } else {
            this.missingFacetProfile = true;
            this.facetsLoading$.stopLoading();
        }
    }

    /**
     * Pass background restricting query for page-results.
     * Ex. Filtering on published Records and Collections
     */
    @Input() set queryRestrictions(queryRestrictions: Restriction | undefined) {
        this.restrictions$.next(queryRestrictions);
    }

    /**
     * Whether to render the selected-facet summary inside this panel. The record overview turns this off
     * because it shows active filters in the pill bar above its table instead; other surfaces keep it on.
     */
    @Input() showSummary = true;

    /**
     * Whether to render this panel's own "Filteren" title. The record overview turns this off because it
     * renders its own sticky title (next to the export launch button) above the facet list.
     */
    @Input() showTitle = true;

    /** Whether a facetgroep can be folded shut at all. */
    @Input() collapsible = true;

    @Input() collapseFacetsByDefault = true;

    /**
     * Whether to drop filters that cannot narrow the current list (HFBD-5). Default on, and no app call site
     * sets it — it exists as a seam so an unexpected effect can be undone without a revert.
     */
    @Input() hideNonDiscriminatingFacets = true;

    /**
     * The size of the population the facet counts are computed over, which is not always the total the page
     * displays. Leave unset on surfaces that have no trustworthy total: the rule above then never fires.
     *
     * The panel withholds its first render until this has emitted, so no filter appears and then vanishes.
     */
    @Input() set resultTotal$(total$: Observable<number | undefined> | undefined) {
        this.total$.next(total$ ?? of(undefined));
    }

    private readonly total$: BehaviorSubject<Observable<number | undefined>> = new BehaviorSubject<
        Observable<number | undefined>
    >(of(undefined));

    @Input() reorderable = false;

    /**
     * HFBD-6: bodies that replace a facet's value list, by facet key — see
     * {@link ShFacetListComponent.bodyTemplates}. Passing nothing leaves every facet on its stock list.
     */
    @Input() bodyTemplates: ReadonlyMap<string, TemplateRef<ShFacetBodyContext>> | null = null;

    /**
     * HFBD-6: keys the panel must not render even though the profile lists them.
     *
     * ⚠ This is not a duplicate of `HideEmpty` or of the hide-non-discriminating rule, and it cannot be
     * expressed by either. Both of those judge the facet **response** the panel itself requested. A facet
     * drawn by a {@link bodyTemplates} body draws from a *second* call the panel never sees, so only its
     * consumer knows whether that body has anything to offer — for the Submap tree, a dossier whose paths
     * hold no folder at all, while the profile's own segment facet still returns the dossier name and every
     * filename.
     */
    @Input() set suppressedFacetKeys(keys: ReadonlySet<string> | null | undefined) {
        this.suppressedFacetKeys$.next(keys ?? null);
    }

    private readonly suppressedFacetKeys$: BehaviorSubject<ReadonlySet<string> | null> = new BehaviorSubject<
        ReadonlySet<string> | null
    >(null);

    private readonly facetOrder$: BehaviorSubject<readonly string[] | null> = new BehaviorSubject<
        readonly string[] | null
    >(null);

    @Input() set facetOrder(order: readonly string[] | null | undefined) {
        this.facetOrder$.next(order ?? null);
    }

    /**
     * The **full** ranking after a drag, not the keys the user could see. The panel drops filters that
     * cannot narrow the list (HFBD-5), so a consumer that stored the visible order alone would forget where
     * every hidden filter sat.
     */
    @Output() facetsReordered: EventEmitter<string[]> = new EventEmitter<string[]>();

    @Output() resetOrder: EventEmitter<void> = new EventEmitter<void>();

    /**
     * The keys of the filters this panel is showing. A filter and its column-header dropdown are one filter
     * with two front doors sharing the same URL state, so a consumer rendering both uses this to keep them
     * from contradicting each other. Never emitted when no panel is rendered — which is the point.
     */
    @Output() visibleFacetKeys: EventEmitter<string[]> = new EventEmitter<string[]>();

    /**
     * Streams that make the panel ask for its facets again. Optional, because a surface that has nothing
     * to refresh on — the Taken pages — must still be able to render the panel.
     */
    facetRefreshTriggers: InputSignal<Observable<boolean | void>[]> = input<Observable<boolean | void>[]>([]);

    /**
     * Content projected into the panel head. Present only when a consumer marks an element with
     * `shFacetPanelHead`, and the only reason the head renders when both the title and the summary are off.
     */
    protected readonly panelHead: Signal<ShFacetPanelHeadDirective | undefined> =
        contentChild(ShFacetPanelHeadDirective);

    public expandedFacetKeys: ReadonlySet<string> = new Set<string>();
    private expandedKeysInitialized = false;

    /**
     * Filters this panel keeps at the top, open, and out of the drag list. The base pins nothing; the Taken
     * panel pins "Mijn taken", which is the page's primary control.
     */
    protected pinnedFacetKeys: ReadonlySet<string> = new Set<string>();

    private profileFieldKeys: string[] = [];

    constructor(
        protected readonly urlQueryService: ShUrlQueryService,
        private readonly fieldDefinitionService: ShFieldDefinitionService,
        private readonly facetValueTranslateService: ShFacetValueTranslationService,
        private readonly selectedFacetLabelService: ShSelectedFacetLabelService,
        private readonly dialogService: ShDynamicDialogService
    ) {
        super();
    }

    public ngOnInit(): void {
        this.initProfileFieldKeys();
        this.initExtendedProfileStream();
        this.initQueryStream();
        this.initFacetSelectedStream();
        this.initFacetsStream();
    }

    public ngOnDestroy(): void {
        super.ngOnDestroy();
        this.facetsLoading$.complete();
        this.suppressedFacetKeys$.complete();
    }

    private initProfileFieldKeys(): void {
        this.facetProfile$
            .pipe(takeUntil(this.destroy$))
            .subscribe(profile => (this.profileFieldKeys = (profile.Fields ?? []).map(field => field.DottedKey)));
    }

    // #region Extend profile
    // TO IMPROVE: Add ExtendedProfileService to Stylehaven and just pass profile-id/tag into component
    // OR API should provide at least type of field
    private initExtendedProfileStream() {
        this.extendedFacetProfile$ = this.facetProfile$.pipe(
            switchMap(facetProfile => {
                const fielddefinitions$: Observable<BaseNestedFieldDefinition[]> =
                    this.fieldDefinitionService.getByProfileIds(
                        [facetProfile.Id],
                        facetProfile.Fields.map(field => field.DottedKey)
                    );
                return fielddefinitions$.pipe(
                    map(fieldDefinitions => this.extendProfile(facetProfile, fieldDefinitions))
                );
            })
        );
    }

    private extendProfile(profile: FacetProfile, fieldDefinitions: BaseNestedFieldDefinition[]): ExtendedFacetProfile {
        if (!profile.Fields || profile.Fields.length === 0) {
            return profile as ExtendedFacetProfile;
        }

        const extendedProfileFields: ExtendedFacetProfileField[] = profile.Fields.map(field =>
            this.extendProfileField(field, fieldDefinitions)
        );
        return {
            ...profile,
            Fields: extendedProfileFields
        };
    }

    private extendProfileField(
        profileField: FacetProfileField,
        fieldDefinitions: BaseNestedFieldDefinition[]
    ): ExtendedFacetProfileField {
        const rootKey: DottedKey | undefined = FieldDefinitionHelper.getDottedKeyRoot(profileField.DottedKey);
        const rootFieldDefinition: BaseNestedFieldDefinition | undefined = fieldDefinitions.find(
            fieldDefinition => fieldDefinition.DottedKey === rootKey
        );

        const profileFieldDefinition: BaseNestedFieldDefinition | undefined = rootFieldDefinition
            ? FieldDefinitionHelper.getNestedByDottedKey(rootFieldDefinition, profileField.DottedKey)
            : undefined;

        if (!profileFieldDefinition || !rootFieldDefinition) {
            console.warn(`FieldDefinition or root of ${profileField.DottedKey} was not found`);
        }

        return {
            ...profileField,
            FieldDefinition: profileFieldDefinition
        } as ExtendedFacetProfileField;
    }
    // #endregion

    private initQueryStream(): void {
        // startWith, so a trigger that has not fired yet cannot withhold the panel's first query.
        const extraTriggers: Observable<unknown>[] = this.extraQueryTriggers().map(trigger =>
            trigger.pipe(startWith(null))
        );

        this.query$ = combineLatest([
            this.urlQueryService.getStructuredQuery(),
            this.restrictions$,
            ...extraTriggers
        ]).pipe(
            map(([query, restrictions]) => {
                return new ApiQuery(query, restrictions);
            })
        );
    }

    private initFacetSelectedStream(): void {
        this.selectedFacets$ = combineLatest([
            this.urlQueryService.getSelectedFacets(),
            this.extendedFacetProfile$
        ]).pipe(
            takeUntil(this.destroy$), // Switching to a page with cached facets can make this trigger and cause an error
            map(([selectedFacets, facetProfile]) =>
                this.selectedFacetLabelService
                    .translateSelectedFacetLabels(
                        selectedFacets,
                        facetProfile.Fields,
                        this.virtualFacets(selectedFacets)
                    )
                    .sort((a, b) => a.facetLabel.localeCompare(b.facetLabel))
            ),
            shareReplay(1)
        );
    }

    private initFacetsStream(): void {
        const refreshTriggers: Observable<boolean>[] = this.facetRefreshTriggers().map(trigger =>
            trigger.pipe(
                map(() => true),
                startWith(true)
            )
        );

        const apiFacets$: Observable<ShFacet[]> = combineLatest([
            this.query$,
            this.extendedFacetProfile$,
            ...refreshTriggers
        ]).pipe(
            // MED-4283: prevent too frequent calling facet API
            debounceTime(100),
            tap(() => this.facetsLoading$.startLoading()),
            tap(() => this.setError(false)),
            switchMap(([query, facetProfile, _]) =>
                this.fetchFacets(query, facetProfile).pipe(catchError(error => this.onLoadError(error)))
            ),
            withLatestFrom(this.selectedFacets$, this.extendedFacetProfile$),
            map(([facets, selectedFacets, facetProfile]) => {
                const allFacets: ShFacet[] = this.appendVirtualFacets(facets, selectedFacets);
                const translatedFacets: ShFacet[] = this.translateFacets(allFacets, facetProfile);
                return this.hideFacets(translatedFacets, selectedFacets, facetProfile);
            }),
            catchError(error => this.onLoadError(error)),
            takeUntil(this.destroy$),
            shareReplay(1)
        );

        // combineLatest withholds its first emission until every source has produced one, so the panel keeps
        // its loading state until the facets *and* the total are in, and renders once.
        const resultTotal$: Observable<number | undefined> = this.total$.pipe(switchMap(total$ => total$));

        this.selectableFacets$ = combineLatest([
            apiFacets$,
            this.selectedFacets$,
            this.facetOrder$,
            resultTotal$,
            this.suppressedFacetKeys$
        ]).pipe(
            tap(() => this.facetsLoading$.startLoading()),
            map(([facets, selectedFacets, facetOrder, total, suppressed]) =>
                sortFacetsByOrder(
                    this.filterNonSelectedValues(
                        filterDiscriminating(
                            this.dropSuppressedFacets(facets, suppressed),
                            selectedFacets,
                            total,
                            this.hideNonDiscriminatingFacets
                        ),
                        selectedFacets
                    ),
                    facetOrder
                )
            ),
            tap(facets => this.initExpandedKeys(facets)),
            tap(facets => this.visibleFacetKeys.emit(facets.map(facet => facet.key))),
            tap(() => this.facetsLoading$.stopLoading())
        );
    }

    // #region Hooks
    /**
     * Fetches this panel's facets. The base reads the record facets endpoint; the Taken panel reads the
     * tasks endpoint instead.
     */
    protected fetchFacets(query: ApiQuery, facetProfile: ExtendedFacetProfile): Observable<ShFacet[]> {
        const facetRequest: FacetRequest = this.createFacetRequest(query, facetProfile);

        return this.facetsService.getProfileFieldFacets(facetRequest, facetProfile.Fields);
    }

    /**
     * Filters this panel offers that no facet profile lists, and that no facet endpoint returns. The base
     * has none; the Taken panel synthesises "Mijn taken". They are appended to the facets and they label
     * the selected values, so a synthesised filter cannot render in the panel but vanish from the summary.
     *
     * @param selectedFacets What the URL holds, so a panel can still describe a synthesised filter the user
     * has selected even when it would not offer that filter itself.
     */
    protected virtualFacets(selectedFacets: ShSelectedFacetValue[]): ShFacet[] {
        return [];
    }

    /**
     * Extra streams that rebuild the query. The base needs none. The Taken panel needs the selected facets,
     * because "Mijn taken" never appears in the structured query, so removing it changes nothing the base
     * listens to.
     */
    protected extraQueryTriggers(): Observable<unknown>[] {
        return [];
    }

    /** The dialog behind "toon meer", which the Taken panel replaces with its own. */
    protected searchDialogComponent: Type<unknown> = ShProfileFacetSearchDialogComponent;

    protected searchDialogData(
        facetProfileField: ExtendedFacetProfileField,
        allFacetProfileFields: FacetProfileField[],
        selectedFacets: ShSelectedFacetValue[],
        query: ApiQuery,
        facetProfileId: string
    ): ProfileFacetSearchDialogConfig {
        return {
            apply: this.translateService.instant('actions.common.apply'),
            cancel: this.translateService.instant('actions.common.cancel'),
            facetProfileField,
            facetProfileId,
            allFacetProfileFields,
            selectedFacets,
            query
        };
    }
    // #endregion

    private appendVirtualFacets(facets: ShFacet[], selectedFacets: ShSelectedFacetValue[]): ShFacet[] {
        const unselected: ShFacet[] = this.virtualFacets(selectedFacets).filter(
            virtual => !selectedFacets.some(selected => selected.key === virtual.key)
        );

        return unselected.length > 0 ? [...facets, ...unselected] : facets;
    }

    private dropSuppressedFacets(facets: ShFacet[], suppressed: ReadonlySet<string> | null): ShFacet[] {
        if (!suppressed || suppressed.size === 0) {
            return facets;
        }

        return facets.filter(facet => !suppressed.has(facet.key));
    }

    private initExpandedKeys(facets: ShFacet[]): void {
        if (this.expandedKeysInitialized) {
            return;
        }
        this.expandedKeysInitialized = true;
        this.expandedFacetKeys = this.collapseFacetsByDefault
            ? new Set<string>()
            : new Set<string>(facets.map(facet => facet.key));
    }

    private onLoadError(error: Error): Observable<ShFacet[]> {
        console.error('ProfileFacetList:: An error occured loading the facets', error.message);
        this.setError(true);
        this.facetsLoading$.stopLoading();

        return of([]);
    }

    private createFacetRequest(query: ApiQuery, facetProfile: FacetProfile): FacetRequest {
        const request: FacetRequest = createFacetRequest(query, [facetProfile]);

        return request;
    }

    private translateFacets(facets: ShFacet[], facetProfile: ExtendedFacetProfile): ShFacet[] {
        return facets.map(facet => {
            const facetField: ExtendedFacetProfileField | undefined = facetProfile.Fields.find(
                field => field.DottedKey === facet.key
            );
            const translatedValues: ShSelectableFacetValue[] = facet.values.map(facetValue => ({
                ...facetValue,

                label: facetField
                    ? this.facetValueTranslateService.translateValue(
                          facetField,
                          facetValue.value,
                          facetValue.missing ?? false
                      )
                    : facetValue.label
            }));

            return {
                ...facet,
                // HFBD-6: the same override the pill applies, so a filter and its pill cannot disagree.
                label: facetField ? this.facetValueTranslateService.translateLabel(facetField) : facet.label,
                values: translatedValues
            };
        });
    }

    private hideFacets(
        facets: ShFacet[],
        selectedFacets: ShSelectedFacetValue[],
        facetProfile: FacetProfile
    ): ShFacet[] {
        const filteredFacets: ShFacet[] = [];

        facets.forEach(facet => {
            const hasValues: boolean = facet.values.length > 0;
            const selectedFacetsOfFacet: ShSelectedFacetValue[] = selectedFacets.filter(
                selected => selected.key === facet.key
            );

            const hasSelected: boolean = selectedFacetsOfFacet.length > 0;

            // Hide empty facets
            const hideEmpty: boolean =
                facetProfile.Fields.find(field => field.DottedKey === facet.key)?.HideEmpty ?? true;

            if (!hideEmpty || hasValues || hasSelected) {
                filteredFacets.push(facet);
            }
        });

        return filteredFacets;
    }

    private filterNonSelectedValues(facets: ShFacet[], selectedFacets: ShSelectedFacetValue[]): ShFacet[] {
        const filteredFacets: ShFacet[] = facets.map(facet => {
            const selectedFacetsValues: Set<string> = new Set(
                selectedFacets.filter(selected => selected.key === facet.key).map(selected => selected.value)
            );

            const filteredValues: ShSelectableFacetValue[] = facet.values.filter(
                selectableValue => !selectedFacetsValues.has(selectableValue.value)
            );

            return {
                ...facet,
                values: filteredValues
            };
        });

        return filteredFacets;
    }

    // #region List Interactions
    public onClear(): void {
        this.urlQueryService.clearFacetQuery();
    }

    public onFacetsReordered(visibleKeys: string[]): void {
        const currentRanking: readonly string[] = this.facetOrder$.value ?? this.profileFieldKeys;

        this.facetsReordered.emit(mergeFacetRanking(currentRanking, visibleKeys));
    }

    public onResetOrder(): void {
        this.resetOrder.emit();
    }

    public onRemoveFacet(facet: ShSelectedFacetValue): void {
        this.urlQueryService.removeFacetValue(facet);
    }

    public onFacetSelected(facet: ShSelectedFacetValue): void {
        this.urlQueryService.addFacetValue(facet);
    }

    public onFacetExpandToggle(key: string): void {
        const next: Set<string> = new Set(this.expandedFacetKeys);
        if (next.has(key)) {
            next.delete(key);
        } else {
            next.add(key);
        }
        this.expandedFacetKeys = next;
    }

    public onLoadMore(facet: ShFacet, facetProfile: ExtendedFacetProfile): void {
        const facetProfileField = facetProfile.Fields.find(field => field.DottedKey === facet.key);

        if (!facetProfileField) {
            console.error(`Selected facet ${facet.key} not found in facet-profile`);
            return;
        }

        of([])
            .pipe(
                withLatestFrom(this.selectedFacets$, this.query$),
                switchMap(([, selected, query]) =>
                    this.openFacetsDialog(facetProfileField, facetProfile.Fields, selected, query, facetProfile.Id)
                ),
                takeUntil(this.destroy$)
            )
            .subscribe((dialogOutput: ProfileFacetSearchDialogOutputSuccess) => {
                this.urlQueryService.replaceFacetValues(facet, dialogOutput.selectedValues || []);
            });
    }
    // #endregion

    // #region FacetDialog
    private openFacetsDialog(
        facetProfileField: ExtendedFacetProfileField,
        allFacetProfileFields: FacetProfileField[],
        selectedFacets: ShSelectedFacetValue[],
        query: ApiQuery,
        facetProfileId: string
    ): Observable<ProfileFacetSearchDialogOutputSuccess> {
        const config: DialogConfig<ProfileFacetSearchDialogConfig> = {
            title: facetProfileField.DisplayLabel,
            data: this.searchDialogData(
                facetProfileField,
                allFacetProfileFields,
                selectedFacets,
                query,
                facetProfileId
            ),
            options: {
                style: DialogStyle.Wide
            }
        };

        const ref: DialogRef<ProfileFacetSearchDialogOutput> = this.dialogService.open(
            this.searchDialogComponent,
            config
        );

        return ref.afterClosed$.pipe(
            take(1),
            takeUntil(this.destroy$),
            filter(
                (output: ProfileFacetSearchDialogOutput): output is ProfileFacetSearchDialogOutputSuccess =>
                    output.type === 'success' && output.hasOwnProperty('selectedValues')
            ),
            shareReplay(1)
        );
    }
    // #endregion
}
